import { H2 } from "../reusable-components";
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { Grid, Typography, useMediaQuery } from "@mui/material";
import { Syndicate } from "../models/syndicate";
import { ShallowRound } from "../models/round";
import { getNumFixtures } from "../common/roundhelpers";
import SyndicateAvatar from "./syndicateavatar";
import SyndicateListItem from "./syndicatelistitem";
import useAgents from "./useAgents";
import { useEffect, useState } from "react";
import fetchApi from "../backend/fetchApi";
import { themeC } from "../theme/themehelper";
import { Agent } from "../models/agent";
import parse from 'html-react-parser';

const SyndicateList = ({ syndicates, shallowRounds, agentPage, theme }:
  { syndicates: Syndicate[], shallowRounds: ShallowRound[], agentPage: boolean, theme: string }) => {

  const { t } = useTranslation();
  const agents = useAgents();
  const isSmallScreen = useMediaQuery('(max-width: 620px)');
  const [avatars, setAvatars] = useState<JSX.Element[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchImage = async (imageUrl: string) => {
    try {
      const response = await fetchApi(imageUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      return objectUrl;

    } catch (error) {
      console.error("Error fetching image:", error);
    }
  };

  useEffect(() => {
    async function fetchAvatars() {
      // Preload images
      if (!agents) {
        return;
      }
      await Promise.all(
        agents.map(async (agent: Agent) => {
          let imageUrl = agent._links?.avatarImage?.href;
          if (imageUrl === undefined) {
            return undefined;
          }
          let objectUrl = await fetchImage(agent._links?.avatarImage?.href);
          agent.objectUrl = objectUrl;
          return objectUrl;
        })
      );

      // Create avatar components after preloading images
      const avatarElements = agents.map((agent: any) => (
        <SyndicateAvatar
          key={agent.id}
          agentName={agent.name}
          agentId={agent.id}
          objectUrl={agent.objectUrl}
        />
      ));

      setAvatars(avatarElements);
      setLoading(false);
    }

    if (agents) {
      fetchAvatars();
    }

  }, [agents]);


  let perAgent: any = {};

  if (syndicates !== undefined) {
    syndicates.forEach(element => {
      if (typeof perAgent[element.agentName] !== 'undefined') {
        perAgent[element.agentName].push(element);
      } else {
        var list = [];
        list.push(element);
        perAgent[element.agentName] = list;
      }
    });
  }

  for (let index = 0; index < syndicates.length; index++) {

    const syndicate = syndicates[index];

    if (agents) {
      let agent = agents.filter(a => a.id === syndicate.agentId)[0];
      syndicate.ordNo = agent.ordNo;
    }

  }

  let perAgentList: Array<Syndicate[]> = [];

  Object.keys(perAgent).forEach(function (key, index) {
    let list: Syndicate[] = perAgent[key];

    list.sort((a, b) => {
      let roundA = shallowRounds.find(r => r.id === a.roundId.toString());
      let roundB = shallowRounds.find(r => r.id === b.roundId.toString());

      if (!roundA || !roundB) return 0; // If either round missing, keep order

      // Convert openTo to Date (default to now if missing)
      const dateA = new Date(roundA.openTo ?? Date.now());
      const dateB = new Date(roundB.openTo ?? Date.now());

      // --- Adjust "day boundary" to 9 AM ---
      const shiftMs = 9 * 60 * 60 * 1000; // 9 hours in ms
      const shiftedA = new Date(dateA.getTime() - shiftMs);
      const shiftedB = new Date(dateB.getTime() - shiftMs);

      // Get date only, after shifting
      const aDateOnly = new Date(shiftedA.getFullYear(), shiftedA.getMonth(), shiftedA.getDate()).getTime();
      const bDateOnly = new Date(shiftedB.getFullYear(), shiftedB.getMonth(), shiftedB.getDate()).getTime();

      // Compare by "day" (with 9 AM as boundary)
      if (aDateOnly !== bDateOnly) return aDateOnly - bDateOnly;

      // If same adjusted day, compare by fixture count
      const fixturesA = getNumFixtures(roundA.name);
      const fixturesB = getNumFixtures(roundB.name);
      if (fixturesA !== fixturesB) {
        return fixturesB - fixturesA;
      }

      // If same fixture count, compare by actual time of day
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }

      // If same round, compare by stake amount
      if (roundA.id === roundB.id) {
        return Number(b.stakePerShare.amount) - Number(a.stakePerShare.amount);
      }

      return 0; // Equal for sorting purposes
    });

    list = list.sort((a, b) => {
      const diff1 = a.targetNShares - a.nShares;
      const diff2 = b.targetNShares - b.nShares;

      // Objects where a - b = 0 should come last
      if (diff1 === 0 && diff2 !== 0) return 1;
      if (diff2 === 0 && diff1 !== 0) return -1;
      return 0; // Keep relative order for the rest
    })

    perAgentList.push(list);
  });

  perAgentList.sort((a, b) => {
    // Compare the `OrdNo` of the first element in each array
    return a[0].ordNo - b[0].ordNo;
  });

  if (loading) {
    return <></>
  }

  return (

    <Grid container direction="column" sx={{
      background: 'var(--background_1)',
      padding: isSmallScreen ? "10px" : "40px",
      alignItems: "center"
    }}>

      {perAgentList && perAgentList.map((syndicatesForAgent, i) => {

        let realSyndicates = 0;
        let agent = agents?.filter(a => a.id === syndicatesForAgent[0].agentId)[0];
        let avatar = avatars.filter(a => a.props["agentId"] === syndicatesForAgent[0].agentId);

        let syndicateList = syndicatesForAgent.map((syndicate, j) => {

          let round = shallowRounds.filter(r => r.id === syndicate.roundId.toString());

          if (syndicate.agentName.toLowerCase() === 'anders svensson' && syndicate.nShares === syndicate.targetNShares) {
            return <div key={"empty" + j} />;
          }

          if (j > 3 && !agentPage) {
            if (round.length === 1 && round[0].operatorTurnover.currency === syndicate.stakePerShare.currency) {
              realSyndicates++;
            }
            return <div key={"empty" + j} />;
          }

          if (round.length === 1 && round[0].operatorTurnover.currency === syndicate.stakePerShare.currency) {
            realSyndicates++;
            return (
              <SyndicateListItem key={'syndicate' + j} syndicate={syndicate} round={round[0]} theme={theme} isTerminal={false} />
            )
          }
          return <div key={"empty" + j} />;
        });

        if (realSyndicates === 0) {
          return <div key={"none" + i} ></div>;
        }

        return (

          <Grid key={agent?.name + i.toString()} sx={{
            padding: isSmallScreen ? "0px" : "20px",
            width: "100%",
            marginBottom: "40px",
            background: isSmallScreen ? 'inherit' : "var(--syndicate-list-background)"
          }}>
            <Grid sx={{ marginBottom: "20px" }}>
              <Grid item sx={{ marginBottom: "20px" }}>
                <Grid container direction="row" alignItems="center">
                  {avatar && avatar}
                  <Grid item sx={{ marginLeft: isSmallScreen ? "0px" : "10px", paddingTop: "5px" }}>
                    <Grid container direction="column" justifyContent="center">
                      <H2 key='title' sx={{ marginBottom: '10px' }} >{agent?.name ?? ""}</H2>
                      <Typography sx={{ color: 'var(--text-black)' }}>{realSyndicates} {t('active_syndicates')}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {!agentPage && <Link to={'/syndicatesforagent/' + agent?.id}><span>{t('to_agent')}</span></Link>}
              {agentPage && agent && <Typography sx={{ marginTop: "20px", marginBottom: "20px", fontSize: "14px" }}>{parse(agent.profileText)}</Typography>}
              {agentPage && <Link to='/playagent'><span>{t('go_back')}</span></Link>}
            </Grid>
            <Grid container direction={window.theme === themeC.Orange ? "column" : "row"} justifyContent="center" sx={{ flexWrap: "wrap", marginBottom: "10px" }}>
              {syndicateList}
            </Grid>
          </Grid>
        )
      }
      )}

    </Grid >
  );
}

export default SyndicateList;